import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AvisoDeSoporte } from "@/components/AvisoDeSoporte";
import { LectorGlobal } from "@/components/LectorGlobal";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Lock,
  LogOut,
  Menu,
  Moon,
  Search,
  Sparkles,
  Sun,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { negocio, suscripcion } from "@/lib/endpoints";
import { SUSCRIPCION_VENCIDA } from "@/lib/api";
import { CuentaSuspendida } from "@/features/suscripcion/CuentaSuspendida";
import { cx } from "@/components/ui";
import { Logo } from "@/components/Logo";
import { IndicadorConexion } from "@/components/IndicadorConexion";
import { ModalNuevoNegocio } from "@/components/ModalNuevoNegocio";
import { SelectorNegocio } from "@/components/SelectorNegocio";
import { PreguntaDeCambioDeSucursal, useSucursales } from "@/components/SucursalActual";
import { useSucursal } from "@/stores/sucursal";
import { usePlanActual, useTienePremium } from "@/components/Premium";
import { Campanita } from "@/features/avisos/Campanita";
import { Tutorial } from "@/features/tutorial/Tutorial";
import { BotonDeAyuda } from "@/features/ayuda/BotonDeAyuda";
import { useAuth } from "@/stores/auth";
import { useTema } from "@/stores/ui";
import { useBarra } from "@/stores/barraLateral";
import { useEnlacePermitido } from "./menuVisible";
import { BuscadorDelMenu } from "./BuscadorDelMenu";
import { bloquearDesplazamiento } from "@/lib/bloquearDesplazamiento";
import {
  esDeOtroEnlace,
  esExacto,
  GRUPO,
  GRUPOS,
  INICIO,
  SALON,
  TODAS_LAS_SUCURSALES,
  ALMACEN,
  VENDER,
  type Enlace,
} from "./navegacion";


/**
 * Las pantallas que usan TODO el ancho de la ventana.
 *
 * Son las que enseñan una rejilla de cosas para tocar, no texto para leer. En
 * ellas cada píxel de más es una fila más de productos a la vista, y una
 * búsqueda menos.
 *
 * ⚠️ Antes de meter una aquí, la pregunta es: «¿lo que hay dentro se LEE o se
 * TOCA?». Si se lee, el tope de 1.152 px se queda — una línea de texto de
 * 1.500 px es incómoda de seguir, no generosa.
 */
/**
 * Las pantallas que usan TODO el ancho de la ventana, sin techo.
 *
 * Son rejillas de cosas para tocar, donde cada píxel es una fila más de
 * productos a la vista. El resto de pantallas también son anchas —ver
 * `TOPE_NORMAL`— pero con un límite.
 */
const ANCHO_COMPLETO = ["/vender"];

/**
 * El techo de las demás pantallas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTABA EN 1.152 px Y SOBRABA MEDIA PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `max-w-6xl` son 1.152 px. En un monitor de 1.920, descontando la barra
 * lateral quedan 1.632 útiles: se desperdiciaban 480, o sea 240 muertos a cada
 * lado. El socio lo dijo mirando su pantalla: «siento que queda mucho espacio
 * sobrante a los lados en todas las pantallas».
 *
 * ⚠️ PERO NO SE QUITA EL TECHO, SE SUBE. Y el motivo no es estético: una línea
 * de texto de 1.900 px es incómoda de leer — el ojo pierde el renglón al volver
 * a la izquierda, que es por lo que los periódicos usan columnas. Un formulario
 * de ajustes con las etiquetas a un metro de sus casillas tampoco mejora.
 *
 * 1.560 px es donde una tabla de doce columnas cabe entera y una línea de texto
 * todavía se sigue sin esfuerzo.
 */
const TOPE_NORMAL = "max-w-[1560px]";

/** Cada cuánto se pregunta si cambió el acceso. Ver el efecto de abajo. */
const CADA_CUANTO_EL_ACCESO = 30_000;

export function AppLayout() {
  const { user, role, puede } = useAuth();

  // El modal de "crear otro negocio" vive aquí y no dentro del selector porque
  // se abre desde dos sitios —la barra lateral y la cabecera del celular— y dos
  // instancias del mismo modal se pisarían.
  const [creandoNegocio, setCreandoNegocio] = useState(false);

  /*
   * Los interruptores del negocio vienen de la consulta `negocio`, no de la
   * sesión: la sesión solo trae un resumen del negocio (nombre y moneda) para
   * que el token no engorde con datos que cambian.
   *
   * No supone una petición extra: React Query comparte la clave "negocio" con
   * las pantallas de Ajustes y del recibo, así que se responde desde la caché.
   */
  const { data: datosNegocio } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60_000,
  });

  // Para bajar la cabecera móvil cuando el aviso rojo ocupa la franja de arriba.
  const enSoporte = useAuth((s) => s.soporte);

  /*
    ── La cuenta suspendida por falta de pago: una sola pantalla ──
    Se pregunta al entrar, cada 10 minutos, y en el acto si cualquier pantalla
    recibe el error de suscripción vencida (lo avisa `lib/api.ts`). Sin
    conexión la pregunta falla y no se bloquea nada: el servidor igual lo
    impide al sincronizar. Ver `features/suscripcion/CuentaSuspendida.tsx`.
  */
  const tenantActual = useAuth((s) => s.tenant?.id);
  const { data: estadoCuenta, refetch: revisarCuenta } = useQuery({
    queryKey: ["suscripcion-estado", tenantActual],
    queryFn: suscripcion.estado,
    enabled: Boolean(tenantActual),
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
  });
  useEffect(() => {
    const alVencer = () => void revisarCuenta();
    window.addEventListener(SUSCRIPCION_VENCIDA, alVencer);
    return () => window.removeEventListener(SUSCRIPCION_VENCIDA, alVencer);
  }, [revisarCuenta]);

  /*
    ── Los permisos, al día aunque no se toque nada (2026-10-19) ──
    Cada respuesta del servidor trae la huella del acceso, y si cambió la
    sesión se pone al día sola (ver `actualizarAcceso` en `stores/auth.ts`).
    Pero con la Caja abierta y sin vender puede pasar un buen rato sin ninguna
    respuesta, así que se pregunta cada 30 segundos mientras la pantalla está a
    la vista, y al volver a ella. Es una petición sin cuerpo.
  */
  useEffect(() => {
    if (!tenantActual) return;
    const revisar = () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      void negocio.revisarAcceso().catch(() => undefined);
    };
    const cada = window.setInterval(revisar, CADA_CUANTO_EL_ACCESO);
    document.addEventListener("visibilitychange", revisar);
    return () => {
      window.clearInterval(cada);
      document.removeEventListener("visibilitychange", revisar);
    };
  }, [tenantActual]);

  // La sucursal de este aparato, como `key` del contenido: al cambiarla, la
  // pantalla se abre de cero —sin la ventana, el filtro ni la página que
  // tenía de la otra—. Ver `useCambiarDeSucursal`.
  const sucursalDelAparato = useSucursal((s) => s.porNegocio[tenantActual ?? ""] ?? "");

  /*
    Métodos de cobro, para saber si el negocio fía por algún camino.

    ⚠️ SE PIDEN TODOS, INCLUIDOS LOS DESACTIVADOS, Y ANTES SOLO LOS ACTIVOS.

    Ese `true` escondía «Por cobrar» en el peor momento posible. Un negocio que
    fía durante meses y luego apaga las ventas a crédito deja el método
    DESACTIVADO —no borrado, para no romper las ventas viejas— y con eso el
    enlace desaparecía... con las deudas todavía sin cobrar.

    Que el método EXISTA es la señal correcta: solo se crea al encender el
    crédito, así que si está ahí, este negocio fió alguna vez y la pantalla
    tiene algo que decir. Y si ya no le deben nada, la pantalla lo dice — que
    es infinitamente mejor que no poder llegar a ella.

    ⚠️ Y con clave propia: ver la nota de `metodosPago.listar` en endpoints.ts.
  */

  const ajustes = datosNegocio?.settings;

  /*
    ⚠️ EN UN RESTAURANTE EL BOTÓN GRANDE ES «SALÓN», NO «VENDER».

    El dueño lo pidió así, y es de fondo: en un restaurante no se vende de una
    —se sienta gente, se apunta, se manda a cocina y el dinero entra al final—,
    así que el mesero que pulsa «Vender» cae en la pantalla de cobrar de una
    bodega. Le deja cobrar por fuera del salón sin que la mesa se entere.

    El botón grande es el gesto que se repite cincuenta veces por turno: en una
    bodega es cobrar, en un restaurante es abrir el salón.
  */
  const principal =
    (ajustes?.modo_operacion ?? "general") === "restaurante" ? SALON : VENDER;
  /*
    ⚠️ «POR COBRAR» SE ENSEÑA SIEMPRE. YA NO SE CALCULA NADA.

    Esto miraba si el fiado estaba encendido, o los apartados, o si existía
    algún método de crédito. Y lo último no filtraba por ACTIVO — el método
    «Crédito» se crea con el negocio y al apagar el fiado no se borra, solo se
    desactiva, así que la condición era verdadera siempre. El interruptor no
    escondía nada; solo lo parecía.

    El dueño zanjó lo que había debajo: «mejor que por cobrar esté siempre
    activo aunque no tengan ningún método». Y es lo correcto — quien fió alguna
    vez tiene deuda que cobrar aunque hoy tenga el fiado apagado, y quien nunca
    fió abre la pantalla, lee «nadie te debe nada» y ya sabe para qué sirve.

    Esconder una pantalla que dice «no tienes nada aquí» ahorra un renglón y
    cuesta que la función no exista para quien la necesita.
  */

  // Dentro de un almacén, su propia barra (2026-10-01). Ver `ALMACEN`.
  const enAlmacen = useSucursales().actual?.tipo === "almacen";

  // La regla vive en `menuVisible.ts`: Sofi cuenta el mismo menú que se ve.
  const permitido = useEnlacePermitido();
  // ═══════════════════════════════════════════════════════════════════════
  //  ⚠️ EN «TODOS» CAMBIA LA BARRA ENTERA, NO SOLO EL CONTENIDO
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Antes «Todos» era una pantalla más con el menú de la Barbería El Corte
  // todavía a la izquierda. Eso no se sostiene: al lado del resumen de los
  // cuatro negocios había un «Caja» y un «Inventario» que eran de uno solo. El
  // dueño mira las cifras de todo, pulsa «Catálogo» esperando el catálogo de
  // todo, y aterriza en el de la barbería.
  //
  // Elegir «Todos» es un cambio de sitio, igual que cambiar de negocio. Así
  // que la barra pasa a ser la del grupo, y todo lo que hay en ella vuelve a
  // ser de lo mismo.
  const colapsada = useBarra((s) => s.colapsada);
  const cerrados = useBarra((s) => s.cerrados);
  const alternar = useBarra((s) => s.alternar);
  const alternarGrupo = useBarra((s) => s.alternarGrupo);

  const { pathname } = useLocation();
  const enGrupo = pathname === "/grupo" || pathname.startsWith("/grupo/");
  // «Todas las sucursales» (2026-09-29): también cambia la barra entera, por
  // lo mismo que «Todos». Ver `TODAS_LAS_SUCURSALES` en `navegacion.tsx`.
  const enTodasLasSucursales = pathname === "/sucursales" || pathname.startsWith("/sucursales/");
  // Mirando varios sitios a la vez: sin «Vender» ni «Inicio» de uno solo.
  const mirandoVarios = enGrupo || enTodasLasSucursales;
  // Tampoco en un almacén: ahí no se vende, y su «Inicio» es el inventario.
  const sinVenderNiInicio = mirandoVarios || enAlmacen;

  // ═══════════════════════════════════════════════════════════════════════
  //  ⚠️ NO TODAS LAS PANTALLAS QUIEREN EL MISMO ANCHO
  // ═══════════════════════════════════════════════════════════════════════
  //
  // El tope de 1.152 px es CORRECTO para casi todo. Un formulario o un reporte
  // con líneas de texto más largas se vuelve difícil de leer: el ojo pierde el
  // renglón al volver a la izquierda. Por eso los periódicos usan columnas.
  //
  // Pero una rejilla de productos no es texto: son botones para tocar. Ahí el
  // tope solo deja franjas vacías a los lados mientras las tarjetas se
  // apretujan. Con una pantalla de 1.520 px el dueño veía ~180 px muertos a
  // cada lado y tres columnas donde cabían cinco.
  //
  // Así que se decide POR PANTALLA, y no de una vez para todas.
  const aTodoLoAncho = ANCHO_COMPLETO.some(
    (r) => pathname === r || pathname.startsWith(r + "/"),
  );

  // ═══════════════════════════════════════════════════════════════════════
  //  EL CAJÓN DEL CELULAR
  // ═══════════════════════════════════════════════════════════════════════
  //
  // En el teléfono solo cabían CINCO destinos en la barra de abajo, y la
  // aplicación tiene quince. Los otros diez sencillamente NO EXISTÍAN para
  // quien trabaja desde el celular: ni Crédito, ni Devoluciones, ni Equipo,
  // ni Sucursales, ni Monedas. Ninguna forma de llegar.
  //
  // Eso no es «un menú incompleto», es media aplicación apagada — y la
  // mayoría de estos negocios no tienen computadora, solo el teléfono.
  const [cajonAbierto, setCajonAbierto] = useState(false);

  /*
    La lupa de la barra recogida: abre la barra y lleva el cursor al buscador.

    ⚠️ SIN ATAJO DE TECLADO, a propósito. Hubo un Ctrl+K y el dueño lo pidió
    quitar (2026-10-18): «me gustó el buscador pero quítale el acceso directo».
  */
  const [enfocarBusqueda, setEnfocarBusqueda] = useState(0);
  const abrirBusqueda = () => {
    if (colapsada) alternar();
    setEnfocarBusqueda((n) => n + 1);
  };

  // Al cambiar de pantalla se cierra solo. Sin esto, se toca un enlace, la
  // pantalla cambia por detrás y el cajón se queda encima tapándola: parece
  // que no pasó nada y se vuelve a tocar.
  useEffect(() => setCajonAbierto(false), [pathname]);

  // Con el cajón abierto, el fondo no se desplaza. Es el detalle que separa
  // un cajón de verdad de una capa pegada encima: sin esto, al arrastrar
  // dentro del menú se mueve la pantalla de atrás.
  // Contado con las ventanas: ver `bloquearDesplazamiento`.
  useEffect(() => {
    if (!cajonAbierto) return;
    return bloquearDesplazamiento();
  }, [cajonAbierto]);

  // Escape lo cierra: quien viene de una computadora lo intenta sin pensar.
  useEffect(() => {
    if (!cajonAbierto) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCajonAbierto(false);
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [cajonAbierto]);

  const menu = enGrupo
    ? GRUPO
    : enTodasLasSucursales
      ? TODAS_LAS_SUCURSALES
      : enAlmacen
        ? ALMACEN
        : GRUPOS;

  const grupos = menu
    .map((g) => ({ ...g, enlaces: g.enlaces.filter(permitido) }))
    .filter((g) => g.enlaces.length > 0);

  // Lo que el buscador encuentra además de los grupos.
  const sueltos = sinVenderNiInicio ? [] : [principal, INICIO].filter(permitido);

  // Para el celular: la acción principal y hasta 4 destinos más.
  //
  // ⚠️ En «Todos» NO va «Vender». Vender pasa siempre en un sitio concreto, y
  // un botón de vender aquí solo puede llevar a preguntar «¿dónde?».
  const pestanas = (
    sinVenderNiInicio
      ? menu.flatMap((g) => g.enlaces)
      : // ⚠️ `INICIO` va explícito: desde que salió de los grupos, ya no
        // aparece al recorrerlos, y sin esta línea se caería del celular.
        // Y la principal es «Vender» o «Salón» según el modo: la barra de
        // abajo del celular es lo único que ve un mesero, y ahí la primera
        // pestaña tiene que ser adonde va cincuenta veces por turno.
        [principal, INICIO, ...menu.flatMap((g) => g.enlaces)]
  )
    .filter((e) => e.enMovil && permitido(e))
    .slice(0, 5);

  // ⚠️ Menos en soporte: quien entra desde el panel de Softfi a ayudar tiene
  // que poder ver el negocio y salir por el aviso rojo de arriba, que esta
  // pantalla taparía.
  if (estadoCuenta?.bloqueado && !enSoporte) return <CuentaSuspendida estado={estadoCuenta} />;

  return (
    <div className="min-h-dvh bg-bg">
      {/* Va lo PRIMERO de todo y empujando la página, no flotando: un aviso que
          tapa algo es un aviso que la gente aprende a ignorar. Ver
          `AvisoDeSoporte`, que explica por qué no se puede cerrar. */}
      <AvisoDeSoporte />
      {/* El lector de códigos en todas las pantallas, sin tocar nada (2026-10-03). */}
      <LectorGlobal />
      {creandoNegocio && <ModalNuevoNegocio onCerrar={() => setCreandoNegocio(false)} />}

      {/* ================= Barra lateral (escritorio) ================= */}
      {/*
        ═══════════════════════════════════════════════════════════════════
         ⚠️ SE PUEDE RECOGER, Y ESO NO ES UN ADORNO
        ═══════════════════════════════════════════════════════════════════

        Abierta ocupa 17 rem —casi 280 píxeles— **siempre**. En un portátil eso
        es una quinta parte del ancho, comida por una lista que la mayor parte
        del tiempo no se está mirando: quien está cargando productos no necesita
        ver «Caja», «Fiado» y «Monedas» mientras escribe.

        Recogida quedan los iconos, que después de dos días ya no se leen sino
        que se reconocen, y devuelve esos 200 píxeles a la tabla que sí se está
        mirando.
      */}
      <aside
        className={cx(
          // ⚠️ FLOTA a 12 px de los bordes (2026-10-18, el dueño eligió el
          // «panel flotante» entre cinco propuestas). Por eso mide 12 px menos
          // que el hueco que le deja la pantalla (`lg:pl-72` / `lg:pl-20`):
          // margen y panel juntos ocupan lo mismo que la barra de antes.
          "fixed bottom-3 left-3 top-3 z-30 hidden flex-col rounded-[var(--radius-modal)] border border-line bg-lateral lg:flex",
          // ⚠️ SIN transición de ancho, a propósito.
          //
          // Animar el ancho de una barra fija obliga al navegador a
          // recalcular la posición de TODO el contenido en cada fotograma de
          // la animación. En una tabla de doscientos productos eso se ve como
          // un tirón, que es peor que no tener animación — y de paso deja el
          // ancho a medias si algo vuelve a dibujar a mitad del recorrido.
          colapsada ? "w-[4.25rem]" : "w-[17.25rem]",
        )}
      >
        {/* Marca + negocio */}
        <div
          className={cx(
            "flex items-center py-4",
            colapsada ? "justify-center px-2" : "gap-3 px-4",
          )}
        >
          {/*
            ⚠️ Recogida va el MONOCROMO, no el de color.

            El logo de color es la identidad y por eso se queda cuando la barra
            está abierta, que es donde Softfi se presenta. Pero a 32 píxeles y
            solo, dentro de una columna de iconos de una línea, el color se
            convierte en una mancha: pierde el detalle que lo hace reconocible
            y encima desentona con todo lo que tiene debajo.

            El monocromo hereda `currentColor`, así que sale blanco sobre el
            tema oscuro y negro sobre el claro sin tener que elegir nada.
          */}
          <Logo tamano={colapsada ? 30 : 38} mono={colapsada} className="text-ink" />
          {/* El nombre del negocio ES el conmutador. Ver SelectorNegocio.tsx. */}
          {!colapsada && (
            <>
              <SelectorNegocio onCrearNegocio={() => setCreandoNegocio(true)} />
              <Campanita />
            </>
          )}
        </div>

        {/*
          El tirador. Pegado al borde derecho y a media altura: es donde lo
          busca la mano, y ahí no le quita el sitio a ningún enlace.
        */}
        <button
          type="button"
          onClick={alternar}
          aria-label={colapsada ? "Abrir el menú" : "Recoger el menú"}
          title={colapsada ? "Abrir el menú" : "Recoger el menú"}
          className={cx(
            "absolute -right-3 top-[23px] z-10 flex size-6 items-center justify-center rounded-full",
            "border border-line bg-surface text-muted shadow-sm",
            "transition-colors hover:border-accent/50 hover:text-accent",
          )}
        >
          {colapsada ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        {colapsada && (
          <div className="flex justify-center pb-3">
            <button
              type="button"
              onClick={abrirBusqueda}
              title="Buscar pantalla"
              aria-label="Buscar pantalla"
              className="flex size-10 items-center justify-center rounded-[var(--radius-control)] border border-line bg-surface-2 text-muted transition-colors hover:border-line-strong hover:text-ink"
            >
              <Search size={17} />
            </button>
          </div>
        )}
        <BuscadorDelMenu
          grupos={grupos}
          sueltos={sueltos}
          enfocar={enfocarBusqueda}
          oculto={colapsada}
          claseLista="flex-1 overflow-y-auto scroll-fino px-3 pb-2"
        >
          {/*
            La acción principal, destacada.

            ⚠️ En «Todos» no está. Vender pasa siempre en un sitio concreto, y
            un botón de vender mirando cuatro negocios a la vez solo puede
            llevar a preguntar «¿dónde?» — que es exactamente el clic que ya se
            dio para llegar aquí, al revés.
          */}
          {!sinVenderNiInicio && permitido(principal) && (
            <NavLink
              to={principal.a}
              title={principal.etiqueta}
              className={({ isActive }) =>
                cx(
                  "mb-3 flex h-10 items-center justify-center gap-2 rounded-[var(--radius-control)]",
                  "text-sm font-semibold transition-all duration-150 active:scale-[0.98]",
                  isActive
                    ? "bg-accent text-on-accent"
                    : "bg-accent text-on-accent hover:bg-accent-hover",
                )
              }
            >
              <principal.icono size={17} />
              {!colapsada && principal.etiqueta}
            </NavLink>
          )}

          {/*
            ⚠️ INICIO VA AQUÍ, SUELTO, Y ANTES ESTABA DENTRO DE «OPERACIÓN».

            No es una tarea del negocio: es el resumen de todas. Dentro de un
            grupo quedaba al mismo nivel que «Gastos» o «Clientes», y encima
            desaparecía al plegar ese grupo — el resumen del día escondido
            detrás de un triángulo.

            Su sitio es junto a Vender: las dos cosas que se abren sin pensar en
            qué sección viven.
          */}
          {!sinVenderNiInicio && permitido(INICIO) && (
            <div className="mb-2">
              <EnlaceLateral enlace={INICIO} colapsada={colapsada} />
            </div>
          )}

          {grupos.map((g, i) => {
            const plegado = !colapsada && cerrados.includes(g.titulo);
            return (
              <div
                key={g.titulo}
                className={cx(
                  "mb-2",
                  // Recogida no hay títulos: la raya es lo único que separa
                  // una sección de la otra.
                  i > 0 && (colapsada ? "mt-1 border-t border-line pt-2" : "mt-3"),
                )}
              >
                {/*
                  ⚠️ El título de la sección es un BOTÓN, no un rótulo muerto.

                  Quien no tiene equipo ni toca los ajustes pliega
                  «Administrar» una vez y deja de verla. Con la barra recogida
                  no aplica: ahí no hay títulos que plegar, solo iconos.
                */}
                {!colapsada && (
                  <button
                    type="button"
                    onClick={() => alternarGrupo(g.titulo)}
                    aria-expanded={!plegado}
                    className="group/titulo mb-1 flex w-full items-center gap-2.5 px-2 py-1.5 text-left"
                  >
                    <span className="rotulo-lateral">{g.titulo}</span>
                    <span className="h-px flex-1 bg-line" />
                    <ChevronDown
                      size={14}
                      className={cx(
                        "text-muted opacity-60 transition duration-200 group-hover/titulo:text-ink group-hover/titulo:opacity-100",
                        plegado && "-rotate-90",
                      )}
                    />
                  </button>
                )}

                <Desplegable abierto={!plegado} className="space-y-0.5">
                  {g.enlaces.map((e) => (
                    <EnlaceLateral key={e.a} enlace={e} colapsada={colapsada} />
                  ))}
                </Desplegable>
              </div>
            );
          })}

          {/*
            Debajo de Ajustes, y solo si NO es Premium.

            Este es el único sitio de la aplicación donde Softfi se anuncia a sí
            misma, y va aquí por dos razones. Una: se ve siempre, sin depender de
            que a alguien se le ocurra entrar a Ajustes. Dos: es el último
            elemento de la barra, así que no le quita el sitio a nada que el
            cajero necesite para trabajar.

            Y desaparece en cuanto pagan. Seguir enseñándole "hazte Premium" a
            quien ya es Premium es la forma más rápida de que deje de creerse lo
            que le dice la pantalla.
          */}
          {puede("tenant.manage") && !colapsada && <BotonHazteePremium />}
        </BuscadorDelMenu>

        {!colapsada && (
          <div className="px-3 pb-2">
            <IndicadorConexion />
          </div>
        )}

        <MenuUsuario
          nombre={user?.full_name}
          correo={user?.email}
          rol={role}
          colapsada={colapsada}
        />
      </aside>

      {/* ================= Cajón del menú (celular) ================= */}
      {cajonAbierto && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/*
            La sombra de detrás. Tocarla cierra: es lo que intenta todo el
            mundo antes de buscar la equis.
          */}
          <button
            type="button"
            aria-label="Cerrar el menú"
            onClick={() => setCajonAbierto(false)}
            className="absolute inset-0 cursor-default bg-black/50 backdrop-blur-[2px]"
          />

          {/*
            ⚠️ `max-w-[85vw]` y no un ancho fijo.

            En un teléfono estrecho —los de 320 px que todavía se usan— una
            barra de 288 px dejaría 32 px de fondo asomando, y el cajón se
            leería como una pantalla nueva en vez de como una capa encima.
            Ver el borde de lo que hay detrás es lo que hace entender que se
            cierra tocando fuera.
          */}
          <aside
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-lateral shadow-2xl"
            role="dialog"
            aria-label="Menú"
          >
            <div className="flex items-center gap-2.5 border-b border-line px-4 py-3.5 pt-safe">
              <Logo tamano={30} />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
                {datosNegocio?.name ?? "Softfi"}
              </span>
              <button
                type="button"
                onClick={() => setCajonAbierto(false)}
                aria-label="Cerrar el menú"
                className="rounded-[var(--radius-control)] p-2 text-muted transition-colors hover:bg-surface-hover hover:text-ink"
              >
                <X size={18} />
              </button>
            </div>

            <BuscadorDelMenu
              grupos={grupos}
              sueltos={sueltos}
              enfocar={0}
              claseCasilla="px-3 pb-3 pt-3"
              claseLista="flex-1 overflow-y-auto scroll-fino px-3 pb-3"
            >
              {!mirandoVarios && permitido(principal) && (
                <NavLink
                  to={principal.a}
                  className="mb-3 flex h-11 items-center justify-center gap-2 rounded-[var(--radius-control)] bg-accent text-sm font-semibold text-on-accent active:scale-[0.98]"
                >
                  <principal.icono size={17} />
                  {principal.etiqueta}
                </NavLink>
              )}

              {/*
                ⚠️ AQUÍ VAN LOS GRUPOS ENTEROS, SIN RECORTAR.

                Es justo lo contrario que la barra de abajo, que se queda con
                cinco. Este es el sitio donde está TODO, y por eso los grupos
                no se pliegan: en un menú que se abre y se cierra, plegar una
                sección solo añade un toque para llegar a lo mismo.
              */}
              {grupos.map((g, i) => (
                <div
                  key={g.titulo}
                  className={cx("mb-2", i > 0 && "mt-3")}
                >
                  <div className="mb-1 flex items-center gap-2.5 px-2 py-1.5">
                    <span className="rotulo-lateral">{g.titulo}</span>
                    <span className="h-px flex-1 bg-line" />
                  </div>
                  <div className="space-y-0.5">
                    {g.enlaces.map((e) => (
                      <EnlaceLateral key={e.a} enlace={e} />
                    ))}
                  </div>
                </div>
              ))}

              {puede("tenant.manage") && <BotonHazteePremium />}
            </BuscadorDelMenu>

            {/*
              El pie: quién soy y las tres cosas que se fueron de la cabecera.
              Van juntas abajo porque son de la sesión, no de la aplicación.
            */}
            <div className="border-t border-line px-2 py-2.5 pb-safe">
              <div className="flex items-center gap-2.5 px-2">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-[13px] font-semibold text-on-accent">
                  {(user?.full_name ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-ink">{user?.full_name}</p>
                  <p className="truncate text-[11px] text-muted">{etiquetaDeRol(role)}</p>
                </div>
                {/*
                  ⚠️ Aquí NO va «Ajustes»: ya está arriba, en «Administrar».
                  Repetirlo hace dudar de si son dos sitios distintos.
                */}
                <div className="flex shrink-0 items-center">
                  <BotonTema chico />
                  <BotonSalir chico />
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Barra superior (celular) ================= */}
      {/* Debajo del aviso de soporte, que también es `sticky top-0`. La altura
          la publica el propio aviso en `--alto-aviso`, porque en un teléfono
          ocupa dos líneas y darla por fija dejaba esta cabecera medio tapada. */}
      <header
        className="sticky z-30 border-b border-line bg-surface/85 backdrop-blur-xl pt-safe lg:hidden"
        style={enSoporte ? { top: "var(--alto-aviso, 0px)" } : { top: 0 }}
      >
        <div className="flex h-14 items-center justify-between gap-3 px-4">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {/*
              ⚠️ EL BOTÓN DE MENÚ VA PRIMERO, ARRIBA A LA IZQUIERDA.

              Es donde lo busca el pulgar y donde lo tiene todo el mundo. Y es
              la única puerta a las diez pantallas que no caben abajo.
            */}
            <button
              type="button"
              onClick={() => setCajonAbierto(true)}
              aria-label="Abrir el menú"
              aria-expanded={cajonAbierto}
              className="-ml-1.5 rounded-[var(--radius-control)] p-2.5 text-ink-soft transition-colors hover:bg-surface-hover hover:text-ink"
            >
              <Menu size={20} />
            </button>
            <Logo tamano={28} />
            <SelectorNegocio compacto onCrearNegocio={() => setCreandoNegocio(true)} />
          </div>

          {/*
            ⚠️ SOLO DOS ICONOS AQUÍ.

            Antes había cinco —conexión, campana, ajustes, tema, salir— y entre
            ellos y el nombre del negocio no quedaba barra. Ajustes, tema y
            salir se fueron al cajón, que es donde se buscan de todos modos.

            Estos dos se quedan porque contestan preguntas que se hacen EN
            MEDIO de cobrar, y para eso no se abre ningún menú: «¿esto se está
            guardando?» y «¿me llegó algo?».

            La ayuda (2026-10-26) es el tercero, y por lo mismo: «¿cómo se
            hace esto?» se pregunta con la pantalla delante. Solo sale en las
            pantallas que tienen ayuda; en la computadora va en la esquina del
            encabezado de cada pantalla (ver `BotonDeAyuda`).
          */}
          <div className="flex shrink-0 items-center gap-0.5">
            <BotonDeAyuda compacto />
            <IndicadorConexion compacto />
            <Campanita compacto />
          </div>
        </div>
      </header>

      {/* ================= Contenido ================= */}
      {/* El contenido sigue el ancho de la barra: sin esto, al recogerla
          quedaría una franja vacía a la izquierda y no se ganaría nada. */}
      <main className={cx(colapsada ? "lg:pl-20" : "lg:pl-72")}>
        {/*
          `pb-28` en celular deja espacio para la barra de pestañas: sin eso, el
          último elemento de una lista queda tapado y no se puede tocar.
        */}
        <div
          className={cx(
            "mx-auto px-4 pb-28 pt-5 sm:px-6 lg:pb-12 lg:pt-9",
            aTodoLoAncho ? "max-w-none" : TOPE_NORMAL,
          )}
        >
          <Outlet key={sucursalDelAparato} />
        </div>
      </main>

      {/* «¿Cambiar de sucursal?» con una venta en curso, venga de donde venga. */}
      <PreguntaDeCambioDeSucursal />

      {/*
        El tutorial va aquí, dentro del layout: necesita el router para llevar
        al usuario de pantalla en pantalla, y quedar por encima de todo lo demás.
      */}
      <Tutorial />

      {/* ================= Pestañas (celular) ================= */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur-xl pb-safe lg:hidden">
        <div className="flex items-stretch justify-around">
          {pestanas.map((e) => (
            <EnlacePestana key={e.a} enlace={e} destacado={e.a === principal.a} />
          ))}
        </div>
      </nav>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Piezas
// -----------------------------------------------------------------------------

/**
 * "Hazte Premium" al final de la barra lateral.
 *
 * Es pequeño a propósito. Un banner grande con degradado en una herramienta de
 * trabajo se lee como spam y lo primero que aprende el ojo es a saltárselo —y
 * junto con él, todo lo que haya cerca. Esto ocupa lo mismo que un enlace más y
 * dice una sola frase concreta.
 */
function BotonHazteePremium() {
  const plan = usePlanActual();
  if (plan === "premium") return null;

  return (
    <NavLink
      to="/premium"
      className={({ isActive }) =>
        cx(
          "pulsable mb-2 mt-3 flex items-start gap-3 rounded-[var(--radius-card)] border p-3 transition-colors",
          isActive
            ? "border-accent/50 bg-lateral-activo"
            : "border-line bg-surface-2 hover:border-accent/40",
        )
      }
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-accent-soft text-accent">
        <Sparkles size={15} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">Hazte Premium</span>
        <span className="mt-0.5 block text-xs leading-snug text-muted">
          Tu resumen del día por WhatsApp y el análisis de tus márgenes.
        </span>
      </span>
    </NavLink>
  );
}

/**
 * Lo que se abre y se cierra en el menú —las subopciones y las secciones—,
 * con una animación corta.
 *
 * Lo pidió el dueño: «una pequeña animación al contraer y expandir una
 * pestaña, que sea clean y profesional». Por eso es corta (220 ms), sin rebote,
 * y frena al final: se abre como un cajón bien engrasado.
 *
 * ⚠️ Se anima `grid-template-rows` de 0fr a 1fr, y no `height`. La altura
 * «auto» no se puede animar, y medirla con JavaScript en cada lista obliga a
 * recalcular al cambiar de permisos o de sucursal. El truco de la rejilla
 * llega a la altura justa solo.
 *
 * ⚠️ Cerrado SIGUE MONTADO, con `inert`: así puede animarse al cerrar, y el
 * tabulador no se mete en enlaces que no se ven. Quien pidió menos movimiento
 * en su sistema no ve la animación (ver `prefers-reduced-motion` en
 * index.css).
 */
function Desplegable({
  abierto,
  className,
  children,
}: {
  abierto: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cx(
        "grid transition-[grid-template-rows,opacity] duration-[220ms] ease-[cubic-bezier(0.2,0,0,1)]",
        abierto ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
      aria-hidden={!abierto}
      // React 18 no conoce `inert`: va como atributo de texto.
      {...(abierto ? {} : { inert: "" })}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={cx(
            "transition-transform duration-[220ms] ease-[cubic-bezier(0.2,0,0,1)]",
            abierto ? "translate-y-0" : "-translate-y-1",
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * ¿Esta subopción es la pantalla abierta?
 *
 * ⚠️ `NavLink` solo mira la ruta, nunca lo que va tras la `?`.
 *
 * Sin esto, «Calculadora» se pintaba como abierta con solo estar en Monedas —
 * y entonces las dos entradas quedaban resaltadas a la vez, que es exactamente
 * lo que hace que un menú deje de decir dónde estás. Cada dato pedido tiene que
 * estar en la dirección; el de la subopción `porDefecto` vale también si no se
 * dice («Ventas» a secas es «Cada sucursal»).
 */
function subopcionAbierta(h: Enlace, ruta: string, busqueda: string): boolean {
  const [camino, consulta] = h.a.split("?");
  if (ruta !== camino && !ruta.startsWith(camino + "/")) return false;
  if (!consulta) return true;
  const actual = new URLSearchParams(busqueda);
  return [...new URLSearchParams(consulta)].every(
    ([clave, valor]) => actual.get(clave) === valor || (h.porDefecto && !actual.has(clave)),
  );
}

function EnlaceLateral({
  enlace,
  colapsada = false,
}: {
  enlace: Enlace;
  /** Recogida: solo el icono, y la etiqueta pasa al `title`. */
  colapsada?: boolean;
}) {
  const { puede } = useAuth();
  const lugar = useLocation();
  const ruta = lugar.pathname;

  /*
    ⚠️ Los hijos se despliegan SOLOS cuando estás dentro de la sección.

    Lo pidió el dueño: «así alguien que domina el sistema solo da un clic a
    donde tiene que ir y no 2». Y quien ya está en Catálogo lo que quiere es
    saltar a Impuestos, no volver a abrir un menú que él mismo cerró.

    Fuera de la sección se puede abrir a mano, pero nace cerrado: seis enlaces
    de Catálogo permanentemente desplegados empujarían Ajustes fuera de la
    pantalla en un portátil.
  */
  const hijos = (enlace.hijos ?? []).filter((h) => !h.permiso || puede(h.permiso));
  /*
    ⚠️ «Dentro» incluye la propia sección, no solo sus subpáginas.

    Con `startsWith(a + "/")` a secas, Monedas nunca se desplegaba: su única
    hija es la Calculadora, que no es una ruta aparte sino un cuadro que se abre
    encima (`/monedas?calculadora=1`). El resultado era una flecha que no hacía
    nada visible al entrar, y quien ya está en Monedas es justo quien va a
    querer la calculadora.
  */
  const dentro =
    (ruta === enlace.a || ruta.startsWith(enlace.a + "/")) && !esDeOtroEnlace(enlace.a, ruta);
  const [abierto, setAbierto] = useState(dentro);
  useEffect(() => {
    if (dentro) setAbierto(true);
  }, [dentro]);

  const Icono = enlace.icono;

  // `useTienePremium` devuelve true mientras la sesión carga, así que el
  // candado no parpadea al entrar. Un candado que aparece medio segundo después
  // se lee como un fallo.
  const bloqueado = !useTienePremium(enlace.premium ?? "equipo") && Boolean(enlace.premium);

  /*
    ⚠️ UNA SOLA FILA RELLENA.

    Con una subopción abierta y a la vista, la que se rellena es ELLA y la
    sección queda en negrita con su icono en color: dos filas rellenas a la vez
    no dicen dónde se está. Con la lista cerrada (o la barra recogida) no se ve
    la subopción, y entonces se rellena la sección.
  */
  const subopcionALaVista =
    !colapsada && abierto && hijos.some((h) => subopcionAbierta(h, ruta, lugar.search));
  const rellena = (isActive: boolean) => isActive && !subopcionALaVista;

  const principal = (
    <NavLink
      to={enlace.a}
      end={enlace.exacto ?? esExacto(enlace.a)}
      // ⚠️ Con la barra recogida, la etiqueta pasa al `title`: el icono solo
      // no siempre basta la primera semana, y el globo del navegador resuelve
      // la duda sin ocupar sitio.
      title={colapsada ? enlace.etiqueta : undefined}
      className={({ isActive }) =>
        cx(
          "group flex h-10 items-center rounded-[var(--radius-control)] text-sm transition-colors",
          colapsada ? "justify-center px-0" : "gap-[11px] px-2",
          !colapsada && hijos.length > 0 && "pr-9",
          rellena(isActive)
            ? "bg-lateral-fila font-semibold text-ink"
            : isActive || dentro
              ? "font-semibold text-ink hover:bg-lateral-hover"
              : "font-medium text-ink-soft hover:bg-lateral-hover hover:text-ink",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/*
            El icono en su recuadro, que se rellena en la sección abierta: es
            lo que dice dónde se está sin leer, con el rabillo del ojo.
          */}
          <span
            className={cx(
              "flex size-7 shrink-0 items-center justify-center rounded-lg border transition-colors",
              isActive || dentro
                ? "border-accent bg-accent text-on-accent"
                : "border-line bg-surface-2 text-muted group-hover:text-ink-soft",
            )}
          >
            <Icono size={16} />
          </span>
          {!colapsada && (
            <span className="min-w-0 flex-1 truncate">{enlace.etiqueta}</span>
          )}
          {bloqueado && !colapsada && (
            <Lock size={13} className="shrink-0 text-muted" aria-label="Necesita Premium" />
          )}
        </>
      )}
    </NavLink>
  );

  if (colapsada || hijos.length === 0) return principal;

  return (
    <div>
      <div className="relative">
        {principal}
        {/*
          La flecha va ENCIMA del enlace y no dentro: metida dentro, tocarla
          navegaría además de desplegar — y quien solo quería ver qué hay
          dentro acabaría en otra pantalla.
        */}
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-label={abierto ? `Cerrar ${enlace.etiqueta}` : `Abrir ${enlace.etiqueta}`}
          aria-expanded={abierto}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted transition-colors hover:bg-lateral-hover hover:text-ink"
        >
          <ChevronDown
            size={14}
            className={cx("transition-transform duration-200", abierto && "rotate-180")}
          />
        </button>
      </div>

      {/* La raya de las subopciones pasa por el centro del recuadro de arriba. */}
      <Desplegable
        abierto={abierto}
        className="mb-1 ml-[1.375rem] mt-0.5 space-y-0.5 border-l border-line-strong pl-2"
      >
        {hijos.map((h) => {
          const IconoHijo = h.icono;
          const activo = subopcionAbierta(h, ruta, lugar.search);
          return (
            <NavLink
              key={h.a}
              to={h.a}
              className={cx(
                "flex h-8 items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 text-[13px] transition-colors",
                activo
                  ? "bg-lateral-activo font-semibold text-lateral-activo-ink"
                  : "text-ink-soft hover:bg-lateral-hover hover:text-ink",
              )}
            >
              <IconoHijo size={15} className={cx("shrink-0", activo ? "text-accent" : "text-muted")} />
              <span className="min-w-0 flex-1 truncate">{h.etiqueta}</span>
            </NavLink>
          );
        })}
      </Desplegable>
    </div>
  );
}

function EnlacePestana({ enlace, destacado }: { enlace: Enlace; destacado?: boolean }) {
  const Icono = enlace.icono;
  return (
    <NavLink
      to={enlace.a}
      end={enlace.exacto ?? esExacto(enlace.a)}
      className={({ isActive }) =>
        cx(
          // min-h-14: zona de toque cómoda para el pulgar
          "relative flex min-h-14 flex-1 flex-col items-center justify-center gap-1 px-1 pb-1.5 pt-2",
          "transition-colors",
          isActive ? "text-accent" : "text-muted",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/*
            En celular el indicador va ARRIBA y no como fondo: la pestaña activa
            tiene que reconocerse aunque el pulgar esté tapando el icono, que es
            lo que pasa justo en el momento de tocarla.
          */}
          <span
            className={cx(
              "absolute inset-x-4 top-0 h-[2.5px] rounded-b-full transition-opacity",
              isActive ? "bg-accent opacity-100" : "opacity-0",
            )}
          />
          <span
            className={cx(
              "flex size-7 items-center justify-center rounded-[10px] transition-colors",
              // La pestaña de vender lleva fondo propio: es la que se busca sin mirar.
              destacado && !isActive && "bg-accent-soft text-accent",
              isActive && "bg-accent-soft",
            )}
          >
            <Icono size={19} />
          </span>
          <span className="text-[10px] font-medium leading-none">{enlace.etiquetaCorta ?? enlace.etiqueta}</span>
        </>
      )}
    </NavLink>
  );
}

/** `chico`: la versión del pie del menú, que va dentro de una tarjeta compacta. */
function BotonTema({ chico = false }: { chico?: boolean }) {
  const { tema, alternar } = useTema();
  const esOscuro = tema === "dark";

  return (
    <button
      type="button"
      onClick={alternar}
      aria-label={esOscuro ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
      title={esOscuro ? "Modo claro" : "Modo oscuro"}
      className={cx(
        "rounded-[var(--radius-control)] text-muted transition-colors hover:bg-surface-hover hover:text-ink",
        chico ? "p-2" : "p-2.5",
      )}
    >
      {esOscuro ? <Sun size={chico ? 16 : 18} /> : <Moon size={chico ? 16 : 18} />}
    </button>
  );
}

function BotonSalir({ chico = false }: { chico?: boolean }) {
  const cerrarSesion = useAuth((s) => s.cerrarSesion);
  const navegar = useNavigate();
  const [saliendo, setSaliendo] = useState(false);

  return (
    <button
      type="button"
      disabled={saliendo}
      onClick={async () => {
        setSaliendo(true);
        await cerrarSesion();
        navegar("/entrar", { replace: true });
      }}
      aria-label="Cerrar sesión"
      title="Cerrar sesión"
      className={cx(
        "rounded-[var(--radius-control)] text-muted transition-colors hover:bg-danger-soft hover:text-danger",
        chico ? "p-2" : "p-2.5",
      )}
    >
      <LogOut size={chico ? 16 : 18} />
    </button>
  );
}

/**
 * Quién está usando Softfi, abajo del todo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ RECOGIDA NO SE ESCONDE: SE CONVIERTE EN LA INICIAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Con la barra recogida esto se salía: el nombre, el correo, el rol y dos
 * botones apretados en 80 píxeles, desbordando sobre el contenido. Feo, y de
 * los que se notan.
 *
 * La salida fácil era esconderlo entero. No sirve: **salir de la sesión es lo
 * único que no puede quedar sin puerta**. En una tablet de mostrador que usan
 * tres personas por turno, cerrar sesión es de lo que más se toca — y si el
 * único camino fuera desplegar la barra primero, el cajero deja la sesión del
 * otro abierta y cobra a nombre suyo.
 *
 * Así que queda la inicial, que es lo que de verdad hay que poder mirar de un
 * vistazo (¿quién está aquí?), y al tocarla se abre el resto. Es el patrón que
 * ya conoce cualquiera y no inventa nada.
 */
function MenuUsuario({
  nombre,
  correo,
  rol,
  colapsada = false,
}: {
  nombre?: string;
  correo?: string;
  rol: string | null;
  colapsada?: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const inicial = (nombre ?? "?").charAt(0).toUpperCase();

  if (colapsada) {
    return (
      <div className="relative p-2">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          title={nombre ? `${nombre} · ${etiquetaDeRol(rol)}` : "Tu cuenta"}
          aria-label="Tu cuenta"
          aria-expanded={abierto}
          className={cx(
            "mx-auto flex size-10 items-center justify-center rounded-full",
            "text-sm font-semibold transition-colors",
            abierto
              ? "bg-accent text-on-accent ring-2 ring-accent/30 ring-offset-2 ring-offset-lateral"
              : "bg-accent text-on-accent hover:bg-accent-hover",
          )}
        >
          {inicial}
        </button>

        {abierto && (
          <>
            {/* Un tapiz invisible para cerrarlo tocando fuera. Sin esto hay que
                volver a dar exactamente en la inicial, que es de lo que más
                molesta de un menú. */}
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setAbierto(false)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              className={cx(
                "absolute bottom-2 left-full z-50 ml-2 w-56 overflow-hidden",
                "rounded-[var(--radius-card)] border border-line bg-surface",
                "shadow-[var(--shadow-pop)]",
              )}
            >
              <div className="border-b border-line px-3 py-2.5">
                <p className="truncate text-sm font-medium text-ink">{nombre ?? "—"}</p>
                <p className="truncate text-xs text-muted">{correo ?? ""}</p>
                <p className="mt-0.5 text-[11px] text-faint">{etiquetaDeRol(rol)}</p>
              </div>
              <div className="flex items-center justify-around p-1.5">
                <BotonTema />
                <BotonSalir />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-2 py-1 pl-1.5 pr-0.5">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-[13px] font-semibold text-on-accent">
          {inicial}
        </div>
        {/* El rol va junto al nombre, que es lo que se mira («¿quién está
            aquí y qué puede hacer?»); el correo entero, al pasar el ratón. */}
        <div className="min-w-0 flex-1" title={correo ?? undefined}>
          <p className="truncate text-[13px] font-semibold text-ink">{nombre ?? "—"}</p>
          <p className="truncate text-[11px] text-muted">
            {etiquetaDeRol(rol)}
            {correo ? ` · ${correo}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          <BotonTema chico />
          <BotonSalir chico />
        </div>
      </div>
    </div>
  );
}

/**
 * Traduce el rol técnico a algo que el usuario entienda.
 *
 * El rol se llama `admin` por dentro (nombre técnico que no cambiamos para no
 * romper datos existentes), pero se muestra como **Supervisor**: es la palabra
 * que usa un negocio real, y deja claro que puede haber varios.
 */
export function etiquetaDeRol(rol: string | null | undefined): string {
  switch (rol) {
    case "owner":
      return "Dueño";
    case "admin":
      return "Supervisor";
    case "employee":
      return "Empleado";
    default:
      return "—";
  }
}
